import queryString from "query-string";

import { hideToast, showToast } from "app/actions/toast_actions";
import { ABORT_ERROR_NAME } from "app/pages/sales-intelligence/constants/common";
import { assignToUserAsync } from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/actions-creators";
import { assignLeadsToUserWithToastThunk } from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/effects";
import type { AssignableUser } from "app/pages/sales-intelligence/sub-modules/assign-to-users/types/common";
import * as opportunitiesActionCreators from "app/pages/sales-intelligence/sub-modules/opportunities/store/action-creators";
import { updateListOpportunitiesWithSuccessModalThunk } from "app/pages/sales-intelligence/sub-modules/opportunities/store/effects";
import type { OpportunityListType } from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";
import { exportWebsitesToExcelService } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/services/exportExcelService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import type { CompetitiveParams } from "../types/common";

import * as ac from "./action-creators";

export function fetchCompetitiveResultsThunk(params: CompetitiveParams) {
    return async function fetchCompetitiveResults(
        dispatch: ThunkDispatchCommon,
        _: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(ac.fetchResultsAsync.request());
        try {
            const response = await deps.si.api.competitiveLandscape.fetchCompetitiveResults(
                queryString.stringify(params),
            );

            return dispatch(ac.fetchResultsAsync.success(response));
        } catch (e) {
            if (e?.name !== ABORT_ERROR_NAME) {
                return dispatch(ac.fetchResultsAsync.failure(e));
            }
        }
    };
}

export const getTopDomainsCompetitiveThunk =
    (params: CompetitiveParams, numberOfDomains: number) =>
    async (dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies): Promise<string[]> => {
        try {
            const response = await deps.si.api.competitiveLandscape.fetchCompetitiveResults(
                queryString.stringify({ ...params, page: 1, pageSize: numberOfDomains }),
            );
            return response.Data.map((row) => row.Domain).filter((domain) => domain !== "grid.upgrade");
        } catch (e) {
            return [];
        }
    };

export const addDomainsToListThunk =
    (list: OpportunityListType, domains: string[]) => async (dispatch: ThunkDispatchCommon) => {
        const dto = { opportunities: domains };
        await dispatch(updateListOpportunitiesWithSuccessModalThunk(list, dto));
    };

export const addTopNDomainsToListThunk =
    (list: OpportunityListType, numberOfDomains: number, params: CompetitiveParams) =>
    async (dispatch: ThunkDispatchCommon) => {
        dispatch(opportunitiesActionCreators.updateListOpportunitiesAsync.request());

        const domains = await dispatch(getTopDomainsCompetitiveThunk(params, numberOfDomains));

        if (domains.length > 0) {
            const dto = { opportunities: domains };
            await dispatch(updateListOpportunitiesWithSuccessModalThunk(list, dto));
        } else {
            dispatch(opportunitiesActionCreators.updateListOpportunitiesAsync.failure("No domains."));
        }
    };

export const downloadExcelThunk =
    (domains: string[], params: CompetitiveParams) =>
    async (dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(ac.downloadAsExcelAsync.request());
        const { createToast, getExportingToastTitle, getSuccessToastTitle, getErrorToastTitle, getToastComponent } =
            exportWebsitesToExcelService;
        const toast = createToast(getExportingToastTitle(domains.length));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        const result = await deps.si.api.competitiveLandscape.downloadExcel(domains, queryString.stringify(params));

        dispatch(hideToast(toast));

        if (result.success) {
            dispatch(exportToExcelToastThunk(getSuccessToastTitle()));
            return dispatch(ac.downloadAsExcelAsync.success());
        }
        dispatch(exportToExcelToastThunk(getErrorToastTitle()));
        return dispatch(ac.downloadAsExcelAsync.failure(result.error));
    };

export const downloadExcelTopNDomainsThunk =
    (numberOfDomains: number, params: CompetitiveParams) =>
    async (dispatch: ThunkDispatchCommon, _: ThunkGetState, deps: ThunkDependencies) => {
        dispatch(ac.downloadAsExcelAsync.request());
        const { createToast, getExportingToastTitle, getSuccessToastTitle, getErrorToastTitle, getToastComponent } =
            exportWebsitesToExcelService;
        const toast = createToast(getExportingToastTitle(numberOfDomains));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        const domains = await dispatch(getTopDomainsCompetitiveThunk(params, numberOfDomains));

        if (domains.length > 0) {
            const result = await deps.si.api.competitiveLandscape.downloadExcel(domains, queryString.stringify(params));

            if (result.success) {
                dispatch(hideToast(toast));
                dispatch(exportToExcelToastThunk(getSuccessToastTitle()));
                return dispatch(ac.downloadAsExcelAsync.success());
            }
            return dispatch(ac.downloadAsExcelAsync.failure(result.error));
        } else {
            dispatch(hideToast(toast));
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            dispatch(ac.downloadAsExcelAsync.failure("No domains."));
        }
    };

export function assignTopNDomainsToUserWithToastThunk(
    numberOfLeads: number,
    user: Readonly<AssignableUser>,
    params: CompetitiveParams,
) {
    return async function assignTopNDomainsToUserWithToast(dispatch: ThunkDispatchCommon) {
        dispatch(assignToUserAsync.request());

        try {
            const domains = await dispatch(getTopDomainsCompetitiveThunk(params, numberOfLeads));

            if (domains.length > 0) {
                await dispatch(
                    assignLeadsToUserWithToastThunk({
                        assigneeId: user.id,
                        domains,
                    }),
                );
            }

            return domains;
        } catch (e) {
            console.error(e);
            await dispatch(assignToUserAsync.failure(e));
        }

        return null;
    };
}
